import { Link, createFileRoute } from '@tanstack/react-router'
import { ArrowLeft, ExternalLink, Loader2, TriangleAlert } from 'lucide-react'
import { PageTitle } from '@/components/PageTitle'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { useMuxAccountUsage } from '@/hooks/repositories/useLearningRepository'
import { requirePermission } from '@/lib/permissions/route-guards'
import { BACKOFFICE_PERMISSION_KEYS } from '@/lib/permissions/backoffice-permissions'

export const Route = createFileRoute('/_app/backoffice/integrations/mux/')({
  component: RouteComponent,
  beforeLoad: requirePermission(BACKOFFICE_PERMISSION_KEYS.mux),
})

/**
 * Published allowances, from mux.com/docs/pricing (checked 2026-09-01).
 *
 * Hard-coded because Mux exposes no API for them. If they change, this page
 * quietly misleads — so they live here, together and labelled, rather than
 * scattered through the copy.
 */
const FREE_DELIVERY_MINUTES = 100_000
const FREE_ROBOTS_UNITS = 100_000

/** From the pricing page: per job, plus per minute of content. */
const ROBOTS_UNIT_RATES: Record<
  string,
  { perJob: number; perMinute: number } | undefined
> = {
  summarize: { perJob: 500, perMinute: 30 },
  'generate-chapters': { perJob: 300, perMinute: 50 },
  'find-key-moments': { perJob: 2000, perMinute: 400 },
}

/**
 * Mux usage and where the charges begin (sc-1335).
 *
 * **This is not the invoice.** Mux has no billing API — no plan, no credit
 * balance, no line items are readable. So this counts what can be read and
 * sets it against the published thresholds, which answers "are we close to
 * paying for something" without pretending to be the bill.
 */
function RouteComponent() {
  const { data: usage, isPending } = useMuxAccountUsage()

  const robotsJobs = usage?.robotsJobs ?? []
  const totalJobs = robotsJobs.reduce((sum, row) => sum + row.count, 0)

  // A rough unit figure from the published rates and our average video length.
  // Labelled an estimate because Mux does not report units per job.
  const averageMinutes =
    usage && usage.assets > 0 ? usage.storedMinutes / usage.assets : 0
  const estimatedUnits = robotsJobs.reduce((sum, row) => {
    const rate = ROBOTS_UNIT_RATES[row.workflow]
    if (!rate) return sum
    return sum + row.count * (rate.perJob + rate.perMinute * averageMinutes)
  }, 0)

  return (
    <div className="py-4">
      <PageTitle title="Backoffice — Mux" />

      <Button asChild variant="ghost" size="sm" className="mb-3 -ml-2">
        <Link to="/backoffice/integrations">
          <ArrowLeft className="mr-1 size-4" aria-hidden />
          Integrations
        </Link>
      </Button>

      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight sm:text-2xl">Mux</h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Video hosting for Learning. Mux publishes no billing API, so this
            shows what the account is consuming against the free allowances —
            not the invoice. The dashboard is the authority on what is owed.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <a
            href="https://dashboard.mux.com/settings/billing"
            target="_blank"
            rel="noreferrer noopener"
          >
            Billing in Mux
            <ExternalLink className="ml-1.5 size-4" aria-hidden />
          </a>
        </Button>
      </div>

      {isPending ? (
        <div className="flex justify-center py-16">
          <Loader2
            className="size-5 animate-spin text-muted-foreground"
            aria-hidden
          />
        </div>
      ) : !usage?.available ? (
        <Card>
          <CardContent className="flex items-start gap-3 py-6">
            <TriangleAlert
              className="mt-0.5 size-5 shrink-0 text-amber-600"
              aria-hidden
            />
            <div className="text-sm">
              <p className="font-medium">Mux could not be read</p>
              <p className="mt-1 text-muted-foreground">
                Either the credentials are missing in this environment, or the
                API is unavailable. Video playback and uploads will be failing
                too.
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {/* ── Where charges actually begin ────────────────────────── */}
          <Card>
            <CardContent className="space-y-4 py-5">
              <div>
                <h2 className="text-sm font-semibold">
                  What accrues, and from when
                </h2>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Plan credits are consumed first — on the Launch plan, $100 of
                  usage a month for $20. Only what exceeds them is invoiced.
                </p>
              </div>

              <Line
                label="Storage"
                detail={`${usage.storedMinutes.toLocaleString()} minutes stored across ${usage.assets} videos`}
                note="Charges from the first minute — this is the one that always accrues. About $0.003 per minute per month at 1080p, dropping 60% after 90 days."
              />

              <Line
                label="Delivery"
                detail={`${usage.deliveredMinutes.toLocaleString()} of ${FREE_DELIVERY_MINUTES.toLocaleString()} free minutes, last 30 days`}
                percent={(usage.deliveredMinutes / FREE_DELIVERY_MINUTES) * 100}
                note="Free to 100,000 minutes a month. Fifty staff working through a ten-hour library is around 30,000."
              />

              <Line
                label="Encoding"
                detail="Free — we upload at basic quality only"
                note="Basic quality costs nothing to encode. Plus and Premium would; we do not use them."
              />

              <Line
                label="Mux Robots"
                detail={`${totalJobs} job${totalJobs === 1 ? '' : 's'} · roughly ${Math.round(estimatedUnits).toLocaleString()} of ${FREE_ROBOTS_UNITS.toLocaleString()} free units`}
                percent={(estimatedUnits / FREE_ROBOTS_UNITS) * 100}
                note="Transcripts, chapters and summaries. Free to 100,000 units a month. The unit figure is an estimate — Mux does not report units per job, so it is derived from the published rates and our average video length."
              />

              <Line
                label="Mux Data"
                detail="Included"
                note="Views tracked from Mux-hosted video cost nothing."
              />
            </CardContent>
          </Card>

          {/* ── Robots, by workflow ─────────────────────────────────── */}
          {robotsJobs.length > 0 && (
            <Card>
              <CardContent className="py-5">
                <h2 className="mb-3 text-sm font-semibold">
                  Robots jobs, most recent 100
                </h2>
                <ul className="space-y-1.5">
                  {robotsJobs.map((row) => {
                    const rate = ROBOTS_UNIT_RATES[row.workflow]
                    return (
                      <li
                        key={row.workflow}
                        className="flex items-baseline justify-between gap-3 text-sm"
                      >
                        <span>{row.workflow}</span>
                        <span className="text-muted-foreground tabular-nums">
                          {row.count}
                          {rate && (
                            <span className="ml-2 text-xs">
                              {rate.perJob} + {rate.perMinute}/min
                            </span>
                          )}
                        </span>
                      </li>
                    )
                  })}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  )
}

function Line({
  label,
  detail,
  note,
  percent,
}: {
  label: string
  detail: string
  note: string
  percent?: number
}) {
  const capped = percent == null ? null : Math.min(100, Math.max(0, percent))
  return (
    <div className="space-y-1.5 border-t pt-3 first:border-0 first:pt-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">{label}</p>
        <p className="text-sm text-muted-foreground tabular-nums">{detail}</p>
      </div>
      {capped != null && <Progress value={capped} className="h-1.5" />}
      <p className="text-xs text-muted-foreground">{note}</p>
    </div>
  )
}
